import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import Layout from "@/components/Layout";
import { useCart } from "@/contexts/CartContext";
import { formatINR } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="overline text-[#C9A227]">Your bag</div>
        <h1 className="mt-3 text-4xl md:text-5xl font-light text-[#163A2F] tracking-tight">Shopping bag</h1>

        {items.length === 0 ? (
          <div className="text-center py-24 max-w-md mx-auto">
            <ShoppingBag className="w-12 h-12 mx-auto text-[#C9A227]" strokeWidth={1.2} />
            <p className="mt-6 text-[#163A2F]/70 font-light">Your bag is currently empty.</p>
            <Link to="/shop" className="mt-8 inline-block bg-[#163A2F] text-white px-8 py-4 uppercase text-xs tracking-[0.25em]">Start shopping</Link>
          </div>
        ) : (
          <div className="mt-12 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-6">
              {items.map((it) => {
                const key = `${it.product_id}-${it.size}-${it.color}`;
                return (
                  <div key={key} className="flex gap-5 border-b hairline pb-6" data-testid={`cart-row-${it.product_id}`}>
                    <img src={it.image} alt={it.name} className="w-24 h-32 object-cover bg-[#163A2F]/5" />
                    <div className="flex-1">
                      <div className="flex justify-between gap-3">
                        <div>
                          <h3 className="text-[#163A2F] font-medium">{it.name}</h3>
                          <div className="text-xs text-[#163A2F]/60 mt-1">{it.size} · {it.color || "Default"}</div>
                        </div>
                        <div className="text-[#163A2F] font-medium">{formatINR(it.price * it.quantity)}</div>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="inline-flex items-center border hairline">
                          <button onClick={() => updateQty(key, it.quantity - 1)} className="px-3 py-2"><Minus className="w-4 h-4" /></button>
                          <span className="px-4 py-2">{it.quantity}</span>
                          <button onClick={() => updateQty(key, it.quantity + 1)} className="px-3 py-2"><Plus className="w-4 h-4" /></button>
                        </div>
                        <button onClick={() => removeItem(key)} className="text-[#163A2F]/60 hover:text-[#163A2F] flex items-center gap-2 text-sm">
                          <Trash2 className="w-4 h-4" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <aside className="lg:col-span-4 border hairline p-6 h-fit sticky top-28">
              <div className="overline text-[#C9A227] mb-4">Order summary</div>
              <div className="space-y-3 text-[#163A2F]">
                <div className="flex justify-between text-sm"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
                <div className="flex justify-between text-sm"><span>Shipping</span><span className="text-[#163A2F]/60">Calculated at checkout</span></div>
              </div>
              <div className="my-5 gold-rule" />
              <div className="flex justify-between text-lg font-medium text-[#163A2F]">
                <span>Total</span><span>{formatINR(subtotal)}</span>
              </div>
              <button
                onClick={() => navigate("/checkout")}
                data-testid="proceed-checkout"
                className="mt-6 w-full bg-[#163A2F] text-white py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#0A1C16] transition-colors"
              >
                Proceed to checkout
              </button>
              <Link to="/shop" className="mt-3 block text-center text-sm text-[#163A2F]/70 hover:text-[#163A2F]">Continue shopping</Link>
            </aside>
          </div>
        )}
      </div>
    </Layout>
  );
}
